import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import {
  convertDiagnosesFlatAnswersToFormData,
  convertOtherDiagnosesToFlatAnswers,
  convertPrimaryDiagnosisToFlatAnswers
} from 'components/Assessments/helpers.uncontrolled'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SectionAdapter } from 'components/Assessments/adapters/types'

/**
 * Background subsection adapter
 * Handles diagnoses, equipment, allergies, and other background medical info (12 fields)
 */
export class BackgroundAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Extract diagnosis data using existing helper
    const { primaryDiagnosis, otherDiagnoses } = convertDiagnosesFlatAnswersToFormData(
      getAnswerValuesFromItems(
        flatAnswersArray,
        RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS
      ),
      getAnswerValuesFromItems(
        flatAnswersArray,
        RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS
      )
    )

    // Direct field extractions
    const backgroundEquipment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT
    )

    const backgroundEquipmentOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER
    )?.at(0)

    const backgroundDmeProvider = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER
    )?.at(0)

    const backgroundAllergies = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES
    )

    const backgroundAllergiesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES
    )?.at(0)

    const backgroundWaivers = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS
    )

    const backgroundAdditionalServices = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES
    )

    const backgroundPrognosis = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS
    )?.at(0)

    const backgroundDnr = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR
    )?.at(0)

    const backgroundAdvanceDirectives = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES
    )?.at(0)

    const backgroundAdvanceDirectivesNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES
    )?.at(0)

    return {
      [RecertFormField.BACKGROUND_PRIMARY_DIAGNOSIS]: primaryDiagnosis,
      [RecertFormField.BACKGROUND_OTHER_DIAGNOSIS]: otherDiagnoses,
      [RecertFormField.BACKGROUND_EQUIPMENT]: backgroundEquipment,
      [RecertFormField.BACKGROUND_EQUIPMENT_OTHER]: backgroundEquipmentOther,
      [RecertFormField.BACKGROUND_DME_PROVIDER]: backgroundDmeProvider,
      [RecertFormField.BACKGROUND_ALLERGIES]: backgroundAllergies,
      [RecertFormField.BACKGROUND_ALLERGIES_NOTES]: backgroundAllergiesNotes,
      [RecertFormField.BACKGROUND_WAIVERS]: backgroundWaivers,
      [RecertFormField.BACKGROUND_ADDITIONAL_SERVICES]: backgroundAdditionalServices,
      [RecertFormField.BACKGROUND_PROGNOSIS]: backgroundPrognosis,
      [RecertFormField.BACKGROUND_DNR]: backgroundDnr,
      [RecertFormField.BACKGROUND_ADVANCE_DIRECTIVES]: backgroundAdvanceDirectives,
      [RecertFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]: backgroundAdvanceDirectivesNotes
    }
  }

  buildFlatAnswers(formData: RecertFormData): Array<{ linkId: string; value: unknown }> {
    const mutations: FlatAnswerMutation[] = []
    if (formData[RecertFormField.BACKGROUND_PRIMARY_DIAGNOSIS]) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PRIMARY_DIAGNOSIS,
        value: convertPrimaryDiagnosisToFlatAnswers(
          formData[RecertFormField.BACKGROUND_PRIMARY_DIAGNOSIS]
        )
      })
    }
    if (formData[RecertFormField.BACKGROUND_OTHER_DIAGNOSIS]) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_OTHER_DIAGNOSIS,
        value: convertOtherDiagnosesToFlatAnswers(
          formData[RecertFormField.BACKGROUND_OTHER_DIAGNOSIS] ?? []
        )
      })
    }
    if (formData[RecertFormField.BACKGROUND_EQUIPMENT] !== undefined) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT,
        value: formData[RecertFormField.BACKGROUND_EQUIPMENT]
      })
    }
    if (formData[RecertFormField.BACKGROUND_EQUIPMENT_OTHER] !== undefined) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_EQUIPMENT_OTHER,
        value: formData[RecertFormField.BACKGROUND_EQUIPMENT_OTHER]
      })
    }
    mutations.push(
      ...[
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DME_PROVIDER,
          value: formData[RecertFormField.BACKGROUND_DME_PROVIDER]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES,
          value: formData[RecertFormField.BACKGROUND_ALLERGIES]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ALLERGIES_NOTES,
          value: formData[RecertFormField.BACKGROUND_ALLERGIES_NOTES]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_WAIVERS,
          value: formData[RecertFormField.BACKGROUND_WAIVERS]
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADDITIONAL_SERVICES,
          value: formData[RecertFormField.BACKGROUND_ADDITIONAL_SERVICES]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_PROGNOSIS,
          value: formData[RecertFormField.BACKGROUND_PROGNOSIS]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_DNR,
          value: formData[RecertFormField.BACKGROUND_DNR]
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES,
          value: formData[RecertFormField.BACKGROUND_ADVANCE_DIRECTIVES]
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_BACKGROUND_ADVANCE_DIRECTIVES_NOTES,
          value: formData[RecertFormField.BACKGROUND_ADVANCE_DIRECTIVES_NOTES]
        }
      ]
    )
    return mutations
  }
}
